import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import { toggleIntercom } from 'Shared/helpers';

// Selectors
import * as UserSelectors from 'Shared/selectors/Users.selectors';

// Helpers
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Hooks
import { useReview } from 'Shared/utils/hooks/use-review';

// Components
import { WpDrawerV2, WpDrawerV2Body } from 'Wpds/components/drawer';
import HistoryEvents from 'Shared/components/HistoryEvents/HistoryEvents';

type Props = {
  review;
  isOpen: boolean;
  toggleIsOpen: () => void;
};

const ReviewHistory = ({ review, isOpen, toggleIsOpen }: Props) => {
  const { isPerformanceReview } = useReview({ slug: ReviewsHelpers.getSlug(review) });
  const history = ReviewsHelpers.getHistory(review);
  const createdAt = ReviewsHelpers.getCreatedAt(review);

  const creatorSlug = ReviewsHelpers.getCreatorSlug(review);
  const creator = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: creatorSlug }));
  const creatorFirstName = UserHelpers.getFullName(creator);

  useEffect(() => {
    if (isOpen) {
      toggleIntercom({ show: false });
    } else {
      toggleIntercom({ show: true });
    }
  }, [isOpen]);

  const wordTransform = word => {
    switch (word) {
      case 'compilation':
        return 'Summarizing feedback';
      case 'compilation_completed_but_not_shared':
        return 'Ready, not shared';
      case 'peer_review':
        return 'Reviewing feedback';
      case 'shared':
        return 'Shared';
      case 'cancelled':
        return 'Cancelled';
      case 'completed':
        return 'Completed';
      default:
        return word;
    }
  };

  const createdEvent = [
    {
      title: isPerformanceReview ? 'created review' : 'created 360 feedback',
      timestamp: createdAt,
      userName: creatorFirstName,
    },
  ];

  const events = history.toJS().map(item => {
    const baseEvent = {
      timestamp: item.timestamp,
      userName: item.userName,
    };
    switch (item.changeType) {
      case 'changed_state':
        return {
          ...baseEvent,
          title: 'changed status',
          before: wordTransform(item.before),
          after: wordTransform(item.after),
        };
      case 'changed_receiver_viewed_at':
        if (item.after) return { ...baseEvent, title: `viewed ${isPerformanceReview ? 'review' : '360 feedback'}` };
        break;
      default:
        break;
    }
  });

  return (
    <WpDrawerV2 title="History" isOpen={isOpen} toggleIsOpen={toggleIsOpen}>
      <WpDrawerV2Body>
        <HistoryEvents events={[...createdEvent, ...events]} />
      </WpDrawerV2Body>
    </WpDrawerV2>
  );
};

export default ReviewHistory;
